<script setup lang="ts">
/** Text, password, search, number and multi-line input, sharing one visual treatment. */
withDefaults(
  defineProps<{
    id?: string
    modelValue: string
    type?: 'text' | 'password' | 'email' | 'search' | 'number' | 'url'
    placeholder?: string
    describedBy?: string
    disabled?: boolean
    invalid?: boolean
    autocomplete?: string
    autofocus?: boolean
    /** Renders a textarea instead of an input. */
    rows?: number
    mono?: boolean
    inputmode?: 'text' | 'numeric' | 'decimal'
  }>(),
  { type: 'text', disabled: false, invalid: false },
)

defineEmits<{ 'update:modelValue': [string]; enter: []; input: [string] }>()
</script>

<template>
  <textarea
    v-if="rows"
    :id="id"
    class="o-input"
    :class="{ mono }"
    :rows="rows"
    :value="modelValue"
    :placeholder="placeholder"
    :disabled="disabled"
    :aria-invalid="invalid ? 'true' : undefined"
    :aria-describedby="describedBy"
    @input="
      $emit('update:modelValue', ($event.target as HTMLTextAreaElement).value),
        $emit('input', ($event.target as HTMLTextAreaElement).value)
    "
  />
  <input
    v-else
    :id="id"
    class="o-input"
    :class="{ mono }"
    :type="type"
    :value="modelValue"
    :placeholder="placeholder"
    :disabled="disabled"
    :autocomplete="autocomplete"
    :autofocus="autofocus"
    :inputmode="inputmode"
    :aria-invalid="invalid ? 'true' : undefined"
    :aria-describedby="describedBy"
    @input="
      $emit('update:modelValue', ($event.target as HTMLInputElement).value),
        $emit('input', ($event.target as HTMLInputElement).value)
    "
    @keydown.enter="$emit('enter')"
  />
</template>

<style scoped>
.o-input {
  width: 100%;
  min-width: 0;
  background: var(--bg-surface);
  color: var(--text-strong);
  border: var(--border-width) solid var(--border-strong);
  border-radius: var(--radius-md);
  padding: 0 var(--space-3);
  height: 2.125rem;
  font-size: var(--text-sm);
  transition:
    border-color var(--duration-fast) var(--ease-out),
    box-shadow var(--duration-fast) var(--ease-out);
}

textarea.o-input {
  height: auto;
  padding: var(--space-2) var(--space-3);
  line-height: var(--leading-normal);
  resize: vertical;
}

.o-input::placeholder {
  color: var(--text-subtle);
}

.o-input:focus {
  outline: none;
  border-color: var(--accent);
  box-shadow: 0 0 0 3px var(--accent-ring);
}

.o-input:disabled {
  background: var(--bg-inset);
  color: var(--text-muted);
  cursor: not-allowed;
}

.o-input[aria-invalid='true'] {
  border-color: var(--critical);
}

.mono {
  font-family: var(--font-mono);
  font-size: var(--text-xs);
}
</style>
